// Monday.com OAuth2 — Authorization Code flow
//
// Offline-first notes:
//   - Access tokens expire; refresh tokens are long-lived
//   - Token stored in IndexedDB, survives app restarts
//   - When offline, cached data is served; token refresh is skipped gracefully
//
// OAuth App requirements (user registers their own Monday.com OAuth App):
//   - client_id     — from Monday.com developer > Apps > OAuth section
//   - client_secret — from the same app
//   - redirect_uri  — verigit://oauth/callback (Electron) or <origin>/oauth/callback
//
// Endpoints:
//   Auth URL  : https://auth.monday.com/oauth2/authorize
//   Token URL : https://auth.monday.com/oauth2/token
//   User query: https://api.monday.com/v2  (query { me { id name email } })
//
// Default scopes: me:read boards:read updates:read users:read teams:read

import {
  generateRandomString,
  isElectron,
  proxyUrl,
  parseCallbackUrl,
  openElectronOAuth2Popup,
  openBrowserOAuth2Popup,
  getOAuth2RedirectUri,
  EXPIRY_BUFFER_MS,
} from './platform-oauth2';
import type { PlatformOAuth2Token, PlatformOAuthState } from './types';
import {
  getPlatformOAuth2Token,
  savePlatformOAuth2Token,
  clearPlatformOAuth2Token,
} from './storage';

const MONDAY_AUTH_URL = 'https://auth.monday.com/oauth2/authorize';
const MONDAY_TOKEN_URL = 'https://auth.monday.com/oauth2/token';
const MONDAY_GRAPHQL_URL = 'https://api.monday.com/v2';

export const MONDAY_OAUTH_SCOPES = 'me:read boards:read updates:read users:read teams:read';

const PLATFORM = 'monday' as const;

// ---------------------------------------------------------------------------
// MondayOAuth2Service
// ---------------------------------------------------------------------------

class MondayOAuth2Service {
  private static instance: MondayOAuth2Service;
  private clientId = '';
  private clientSecret = '';
  private redirectUri = '';
  private refreshInProgress = false;
  private stateCallbacks: Set<(state: PlatformOAuthState) => void> = new Set();

  private constructor() {}

  static getInstance(): MondayOAuth2Service {
    if (!MondayOAuth2Service.instance) {
      MondayOAuth2Service.instance = new MondayOAuth2Service();
    }
    return MondayOAuth2Service.instance;
  }

  configure(clientId: string, clientSecret: string, redirectUri?: string): void {
    this.clientId = clientId;
    this.clientSecret = clientSecret;
    this.redirectUri = redirectUri ?? getOAuth2RedirectUri();
    console.log('[MondayOAuth2] Configured', { clientId, redirectUri: this.redirectUri });
  }

  onStateChange(cb: (state: PlatformOAuthState) => void): () => void {
    this.stateCallbacks.add(cb);
    return () => this.stateCallbacks.delete(cb);
  }

  private notifyStateChange(state: PlatformOAuthState): void {
    this.stateCallbacks.forEach((cb) => {
      try { cb(state); } catch (e) { console.error('[MondayOAuth2] State callback error:', e); }
    });
  }

  private computeState(token: PlatformOAuth2Token | null): PlatformOAuthState {
    if (!token) {
      return {
        isAuthenticated: false,
        isExpired: false,
        canRefresh: false,
        isOfflineMode: !navigator.onLine,
        expiresInMs: 0,
      };
    }
    const now = Date.now();
    const expiresInMs =
      token.expiresAt === 0 ? Number.POSITIVE_INFINITY : token.expiresAt - now - EXPIRY_BUFFER_MS;
    return {
      isAuthenticated: true,
      isExpired: token.expiresAt !== 0 && expiresInMs <= 0,
      canRefresh: !!token.refreshToken,
      isOfflineMode: !navigator.onLine,
      expiresInMs,
      token,
    };
  }

  async getState(): Promise<PlatformOAuthState> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    return this.computeState(token);
  }

  // ---------------------------------------------------------------------------
  // Auth flow
  // ---------------------------------------------------------------------------

  private buildAuthUrl(): { url: string; state: string } {
    if (!this.clientId) {
      throw new Error(
        'MondayOAuth2Service not configured — call configure() first with your Monday.com OAuth App credentials'
      );
    }
    const stateParam = generateRandomString(32);
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: MONDAY_OAUTH_SCOPES,
      state: stateParam,
    });
    return { url: `${MONDAY_AUTH_URL}?${params.toString()}`, state: stateParam };
  }

  private async exchangeCodeForTokens(code: string): Promise<PlatformOAuth2Token> {
    console.log('[MondayOAuth2] Exchanging code for tokens...');

    const body = new URLSearchParams({
      client_id: this.clientId,
      client_secret: this.clientSecret,
      code,
      redirect_uri: this.redirectUri,
    });

    const fetchUrl = isElectron ? MONDAY_TOKEN_URL : proxyUrl(MONDAY_TOKEN_URL);
    const response = await fetch(fetchUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`Monday token exchange failed (${response.status}): ${text}`);
    }

    const data = await response.json();
    if (data.error) {
      throw new Error(`Monday OAuth error: ${data.error_description ?? data.error}`);
    }

    const now = Date.now();
    let userId: string | undefined;
    let displayName: string | undefined;
    let email: string | undefined;

    try {
      const gqlFetchUrl = isElectron ? MONDAY_GRAPHQL_URL : proxyUrl(MONDAY_GRAPHQL_URL);
      const meResp = await fetch(gqlFetchUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${data.access_token}`,
          'Content-Type': 'application/json',
          'API-Version': '2024-10',
        },
        body: JSON.stringify({ query: 'query { me { id name email } }' }),
      });
      if (meResp.ok) {
        const meData = await meResp.json();
        const me = meData.data?.me;
        userId = String(me?.id);
        displayName = me?.name;
        email = me?.email;
        console.log('[MondayOAuth2] User profile:', { userId, displayName, email });
      }
    } catch (e) {
      console.warn('[MondayOAuth2] Could not fetch user info:', e);
    }

    const token: PlatformOAuth2Token = {
      id: `${PLATFORM}-oauth2`,
      platform: PLATFORM,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt: data.expires_in ? now + data.expires_in * 1000 : 0,
      scopes: MONDAY_OAUTH_SCOPES.split(' '),
      tokenType: data.token_type ?? 'Bearer',
      userId,
      displayName,
      email,
      issuedAt: now,
      offlineCapable: true,
    };

    await savePlatformOAuth2Token(token);
    this.notifyStateChange(this.computeState(token));
    console.log('[MondayOAuth2] Token saved. ExpiresAt:', token.expiresAt === 0 ? 'never' : new Date(token.expiresAt).toISOString());
    return token;
  }

  async initiateFlow(): Promise<PlatformOAuth2Token> {
    const { url, state } = this.buildAuthUrl();
    let callbackUrl: string;
    if (isElectron) {
      callbackUrl = await openElectronOAuth2Popup(url, this.redirectUri);
    } else {
      callbackUrl = await openBrowserOAuth2Popup(url, 'monday-oauth2-callback');
    }
    const { code } = parseCallbackUrl(callbackUrl, state);
    return this.exchangeCodeForTokens(code);
  }

  // ---------------------------------------------------------------------------
  // Token refresh
  // ---------------------------------------------------------------------------

  async refreshAccessToken(): Promise<PlatformOAuth2Token | null> {
    if (!navigator.onLine) {
      console.log('[MondayOAuth2] Offline — skipping refresh, using cached token');
      return null;
    }
    if (this.refreshInProgress) {
      await new Promise<void>((resolve) => {
        const unsub = this.onStateChange(() => { unsub(); resolve(); });
        setTimeout(() => { unsub(); resolve(); }, 10_000);
      });
      return getPlatformOAuth2Token(PLATFORM);
    }

    const current = await getPlatformOAuth2Token(PLATFORM);
    if (!current?.refreshToken) {
      console.warn('[MondayOAuth2] No refresh token available');
      return null;
    }

    this.refreshInProgress = true;
    console.log('[MondayOAuth2] Refreshing access token...');

    try {
      const body = new URLSearchParams({
        client_id: this.clientId,
        client_secret: this.clientSecret,
        grant_type: 'refresh_token',
        refresh_token: current.refreshToken,
      });

      const fetchUrl = isElectron ? MONDAY_TOKEN_URL : proxyUrl(MONDAY_TOKEN_URL);
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        console.error(`[MondayOAuth2] Refresh failed (${response.status})`);
        return null;
      }

      const data = await response.json();
      const now = Date.now();
      const refreshed: PlatformOAuth2Token = {
        ...current,
        accessToken: data.access_token,
        refreshToken: data.refresh_token ?? current.refreshToken,
        expiresAt: data.expires_in ? now + data.expires_in * 1000 : 0,
        lastRefreshedAt: now,
      };

      await savePlatformOAuth2Token(refreshed);
      this.notifyStateChange(this.computeState(refreshed));
      console.log('[MondayOAuth2] Token refreshed. New expiry:', new Date(refreshed.expiresAt).toISOString());
      return refreshed;
    } finally {
      this.refreshInProgress = false;
    }
  }

  async getAccessToken(): Promise<string | null> {
    const token = await getPlatformOAuth2Token(PLATFORM);
    if (!token) return null;

    const state = this.computeState(token);
    if (!state.isExpired) return token.accessToken;
    if (!navigator.onLine) return token.accessToken; // offline — return stale token

    const refreshed = await this.refreshAccessToken();
    return refreshed?.accessToken ?? null;
  }

  async revokeToken(): Promise<void> {
    await clearPlatformOAuth2Token(PLATFORM);
    this.notifyStateChange(this.computeState(null));
    console.log('[MondayOAuth2] Token revoked and cleared from storage');
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const mondayOAuth2 = MondayOAuth2Service.getInstance();
